// @ts-nocheck
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { DataTable, type ColumnDef } from '@/components/organism/Table';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import GlobalSearch from '@/components/atoms/GlobalSearch';
import Autocomplete from '@/components/elements/AutoComplete';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import DownloadReport from '@/components/organism/ExportDataModal/DownloadReport';
import { getSelectedTimeZone } from '@/utils/dateTime';
import { useDebounce } from '@/hooks/useDebounce';
import useDeepEffect from '@/hooks/useDeepEffect';
import { useExportFormats } from '@/hooks/useExportFormats';
import { useDateFilter } from '@/context/dateFilter.context';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import { getProducts } from '@/store/slices/product.slice';
import { getProductDetail } from '@/store/selectors/product.selector';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  ALL_OPERATOR,
  EActiveStatus,
  STRINGS,
  ALL_PROVIDER,
  DROPDOWN_OPTIONS,
  MODAL_TITLES,
  REPORT_FILE_NAMES,
  COMMON_VALIDATIONS,
  SortEnum,
} from '@/constant/global/global.constant';
import { DEVICE_PROPS } from '@/constant/device/device.constant';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { PRODUCTS_PROPS } from '@/constant/product/product.const';
import {
  EReportFormat,
  EReportLabel,
} from '@/constant/downloadReport/downloadReport.const';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import { ERequestMethods } from '@/constant/downloadReport/downloadReport';
import {
  EAllowedFieldsForExport,
  EExportPage,
} from '@/components/organism/ExportDataModal/export.types';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/products.testIds';
import type { IProductProps } from './products.interface';
import { buildColumns } from './products.columns';
import ProductBarcodeDetailsDialog from './components/ProductBarcodeDetailsDialog';
import ProductMarketDetailsDialog from './components/ProductMarketDetailsDialog';

/* ── Page ─────────────────────────────────────────────────────────────────── */
const Products = () => {
  const dispatch = useDispatch();
  const selectedTimezone = getSelectedTimeZone();
  const exportFormats = useExportFormats(EExportPage.PRODUCTS);
  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;
  const toast = useToast();
  const hasLoaded = useRef(false);

  const [isCopy, setIsCopy] = useState('');
  const [activeStatus, setActiveStatus] = useState<string>('1');
  const [sorting, setSorting] = useState({
    orderBy: 'updatedOn',
    order: SortEnum.desc,
  });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [operatorDetail, setOperatorDetails] = useState({
    provider: ALL_PROVIDER,
    operator: DROPDOWN_OPTIONS.ALL_OPERATOR_DROPDOWN_OPTION,
  });

  const [sourceProductIdSearch, setSourceProductIdSearch] = useState('');
  const [productCodeSearch, setProductCodeSearch] = useState('');
  const [productNameSearch, setProductNameSearch] = useState('');
  const [barcodeSearch, setBarcodeSearch] = useState('');

  const [openProductMarketDialog, setOpenProductMarketDialog] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [openProductBarcodeDialog, setOpenProductBarcodeDialog] =
    useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState(null);

  const {
    loading,
    product: { data, pagination: paginationResponse },
  } = useSelector(getProductDetail);

  // Track first successful data load
  useEffect(() => {
    if (
      !hasLoaded.current &&
      Array.isArray(data?.products) &&
      data.products.length > 0
    ) {
      hasLoaded.current = true;
    }
  }, [data]);

  const sourceProductIdSearchQuery = useDebounce(
    sourceProductIdSearch?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const productNameSearchQuery = useDebounce(
    productNameSearch?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const productCodeSearchQuery = useDebounce(
    productCodeSearch?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );
  const barcodeSearchQuery = useDebounce(
    barcodeSearch?.trim(),
    PROVIDER_CONSTANT_PROPS.GENERIC.DELAY,
  );

  const totalPages = Math.ceil(
    (paginationResponse?.totalItems || 0) / pagination.limit,
  );

  const onCopyClick = useCallback((key: string) => {
    setIsCopy(key);
    setTimeout(() => setIsCopy(''), 500);
  }, []);

  const handleOperatorDetails = useCallback((val: any) => {
    setOperatorDetails({ provider: val?.provider, operator: val?.operator });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPagination((p) => ({
        ...p,
        limit: value,
        page: totalPages < p.page ? totalPages : p.page,
      }));
    },
    [totalPages],
  );

  const handlePreviousClick = useCallback((page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  }, []);

  const handleNextClick = useCallback(
    (page: number) => {
      if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
    },
    [totalPages],
  );

  const handleMarketOpen = useCallback((marketDetails: any) => {
    setSelectedMarket(marketDetails);
    setOpenProductMarketDialog(true);
  }, []);

  const handleBarcodeOpen = useCallback((barcodeDetails: any) => {
    setSelectedBarcode(barcodeDetails);
    setOpenProductBarcodeDialog(true);
  }, []);

  const fetchProductDetails = useCallback(() => {
    if (productNameSearchQuery && productNameSearchQuery.length < 3) {
      toast?.pushError(
        COMMON_VALIDATIONS.INPUT_LENGTH,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
      return;
    }
    dispatch(
      getProducts({
        page: pagination.page,
        limit: pagination.limit,
        order: sorting.order,
        orderBy: sorting.orderBy,
        startDate,
        endDate,
        operatorId: operatorDetail?.operator?.id || ALL_OPERATOR.id,
        includeInactive: activeStatus === 'all' ? true : undefined,
        inactive: activeStatus === '0' ? true : undefined,
        sourceProductId: sourceProductIdSearchQuery,
        name: productNameSearchQuery,
        productCode: productCodeSearchQuery,
        barcode: barcodeSearchQuery,
      }),
    );
  }, [
    pagination,
    sorting,
    startDate,
    endDate,
    operatorDetail,
    activeStatus,
    sourceProductIdSearchQuery,
    productNameSearchQuery,
    productCodeSearchQuery,
    barcodeSearchQuery,
  ]);

  useEffect(() => {
    if (pagination?.page > totalPages && totalPages !== 0) {
      setPagination((p) => ({ ...p, page: 1 }));
    }
  }, [totalPages]);
  const onHandleBarcodeSearch = (value: string) => {
    setBarcodeSearch(value);
  };

  useDeepEffect(
    () => {
      fetchProductDetails();
    },
    [
      pagination,
      sorting,
      startDate,
      endDate,
      operatorDetail?.operator,
      activeStatus,
      productCodeSearchQuery,
      productNameSearchQuery,
      sourceProductIdSearchQuery,
      barcodeSearchQuery,
    ],
    true,
  );

  const allowedFields = EAllowedFieldsForExport[EExportPage.PRODUCTS] || [];
  const columns = useMemo(
    () =>
      buildColumns(isCopy, onCopyClick, handleMarketOpen, handleBarcodeOpen),
    [isCopy, onCopyClick, handleMarketOpen, handleBarcodeOpen],
  );

  return (
    <FullPageCard
      testId={tid(TID.PAGE)}
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.PRODUCTS}
          subtitle={STRINGS.PRODUCTS_SUB}
          icon={getNavItemIcon(NAV_ITEMS.PRODUCTS)}
          actionsSlot={
            <div className="flex items-center gap-2">
              <GlobalSearch
                className="w-[180px]"
                placeholder={
                  PRODUCTS_PROPS.FILTERS.SOURCE_PRODUCT_ID_PLACEHOLDER
                }
                dataTestId={tid(TID.SEARCH_SOURCE_ID)}
                globalSearch={sourceProductIdSearch}
                setGlobalSearch={setSourceProductIdSearch}
                isDataFound={!isEmpty(data)}
              />
              <GlobalSearch
                className="w-[160px]"
                placeholder={PRODUCTS_PROPS.FILTERS.PRODUCT_CODE_PLACEHOLDER}
                dataTestId={tid(TID.SEARCH_CODE)}
                globalSearch={productCodeSearch}
                setGlobalSearch={setProductCodeSearch}
                isDataFound={!isEmpty(data)}
              />
              <GlobalSearch
                className="w-[160px]"
                placeholder={PRODUCTS_PROPS.FILTERS.PRODUCT_NAME_PLACEHOLDER}
                dataTestId={tid(TID.SEARCH_NAME)}
                globalSearch={productNameSearch}
                setGlobalSearch={setProductNameSearch}
                isDataFound={!isEmpty(data)}
              />
              <GlobalSearch
                className="w-[160px]"
                placeholder={PRODUCTS_PROPS.FILTERS.BARCODE_PLACEHOLDER}
                globalSearch={barcodeSearch}
                dataTestId={tid(TID.SEARCH_BARCODE)}
                setGlobalSearch={onHandleBarcodeSearch}
                isDataFound={!isEmpty(data)}
              />
            </div>
          }
          actionButtons={
            <DownloadReport
              testId={tid(TID.EXPORT_BTN)}
              label={EReportLabel.EXPORT_DATA}
              type={EReportFormat.CSV}
              url={API_URL_CONFIG?.PRODUCT?.GET_DOWNLOAD_PDF_CSV}
              orderBy={sorting?.orderBy}
              order={sorting?.order}
              startDate={startDate}
              endDate={endDate}
              timeZone={selectedTimezone}
              limit={paginationResponse?.totalItems}
              inactive={activeStatus === '0' ? true : undefined}
              fileName={REPORT_FILE_NAMES.PRODUCTS_REPORT}
              operatorId={operatorDetail?.operator?.id}
              sourceProductId={sourceProductIdSearchQuery}
              includeInactive={activeStatus === 'all' ? true : undefined}
              name={productNameSearchQuery}
              productCode={productCodeSearchQuery}
              barcode={barcodeSearchQuery}
              modalTitle={MODAL_TITLES.PRODUCTS_PAGE}
              requestMethod={ERequestMethods.POST}
              supportingReportFormats={exportFormats}
              selectiveFieldExport
              allowedFieldsForExport={allowedFields}
            />
          }
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleOperatorDetails}
              showOperator
              showProvider={false}
              showLocation={false}
              dataTestId={tid(TID.FILTER_DROPDOWN)}
            />
          }
          filtersRight={
            <SegmentedControl
              testId={tid(TID.STATUS_FILTER)}
              size="lg"
              shape="square"
              variant="gradient"
              options={[
                { value: 'all', label: PRODUCTS_PROPS.FILTERS.STATUS_ALL },
                { value: '1', label: PRODUCTS_PROPS.FILTERS.STATUS_ACTIVE },
                { value: '0', label: PRODUCTS_PROPS.FILTERS.STATUS_INACTIVE },
              ]}
              value={activeStatus}
              onChange={(v: string) => {
                setActiveStatus(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
            />
          }
        />
      }
    >
      <DataTable
        data-testid={tid(TID.TABLE)}
        data={data?.products ?? []}
        columns={columns}
        loading={loading}
        skeletonRows={15}
        onSort={(orderBy, order) =>
          setSorting({ orderBy, order: order as SortEnum })
        }
        sortState={sorting}
        rowKey={(item: any) => item?.productId ?? ''}
        rowTestId={(item: any, idx: number) =>
          TID.TABLE_ROW(`${idx}`)
        }
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalItems,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
      />

      {/* Dialogs */}
      {selectedMarket && (
        <ProductMarketDetailsDialog
          marketDetails={selectedMarket}
          openProductMarketDialog={openProductMarketDialog}
          handleProductMarketDialogOpen={() =>
            setOpenProductMarketDialog(false)
          }
        />
      )}
      {selectedBarcode && (
        <ProductBarcodeDetailsDialog
          barcodeDetails={selectedBarcode}
          openProductBarcodeDialog={openProductBarcodeDialog}
          handleProductBarcodeDialogOpen={() =>
            setOpenProductBarcodeDialog(false)
          }
        />
      )}
    </FullPageCard>
  );
};

export default Products;
